import React, { useEffect, useState, useCallback, useRef } from "react";
import Topbar from "../../components/Topbar/Topbar";
import { Search_icon } from "../../assets/img";
import DateRangeMUI from "../../components/DateRange/DateRangeMUI";
import { Table } from "../../components/Table/Table";
import { useGlobalContext } from "../../Services/PaginationGlobal";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { get } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";

function AiBookingHistory() {
  const { paginationData, setpaginationData } = useGlobalContext();
  const location = useLocation();
  const path = location.pathname?.replace("/", "");
  const navigate = useNavigate();

  const [count, setCount] = useState(0);
  const [isActive, setIsActive] = useState(
    paginationData?.[path]?.active || "Not Booked",
  );
  const [page, setPage] = useState(paginationData?.[path]?.page ?? 1);
  const [pagination, setPagination] = useState({});
  const [propertiesList, setPropertiesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  // Both stay empty until the user actually picks them.
  // We only send date params to the API once BOTH are set.
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  const pageSize = 10;

  // debounce the search box so we don't fire an API call on every keystroke
  const debounceRef = useRef(null);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearch(value);
    }, 400);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const bookingStatus = isActive === "Booked" ? "booked" : "not_booked";
      const params = new URLSearchParams({
        page: page,
        bookingStatus: bookingStatus,
      });

      // Only send the date range once BOTH from and to are set
      if (fromDate && toDate) {
        params.append("requestFromDate", fromDate);
        params.append("requestToDate", toDate);
      }

      if (search?.trim()) {
        params.append("search", search.trim());
      }

      const res = await get(`${URLS.AI_BOOKING_HISTORY}?${params.toString()}`);

      // API returns either a plain array, or { data: [...] } / { data: { data: [...] } }
      const raw = res?.data ?? res;
      const list = Array.isArray(raw)
        ? raw
        : (raw?.data?.data ?? raw?.data ?? raw?.list ?? []);
      const totalCount =
        res?.total_count ?? res?.count ?? res?.data?.total_count ?? list.length;
      const pageInfo = res?.pagination ??
        res?.data?.pagination ?? {
          total: totalCount,
          page,
          page_size: pageSize,
        };

      setPropertiesList(Array.isArray(list) ? list : []);
      setCount(totalCount || 0);
       setPagination({
         current_page: page,
         last_page: res?.total_pages,
       });
    } catch (error) {
      console.error("Failed to fetch AI booking history:", error);
      setPropertiesList([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [isActive, page, fromDate, toDate, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = React.useMemo(() => {
    let d = [
      {
        Header: "S.No",
        accessor: "S.No",
        Cell: ({ row }) => {
          return <p className="red">{row.index + 1 + (page - 1) * pageSize}</p>;
        },
      },
      {
        Header: "Request ID",
        accessor: "requestId",
        Cell: ({ value }) => {
          return <p className="text-nowrap">{value}</p>;
        },
      },
      {
        Header: "User ID",
        accessor: "userId",
        Cell: ({ value }) => {
          return <p className="text-nowrap">{value}</p>;
        },
      },
      {
        Header: "User Name",
        accessor: "userName",
        Cell: ({ value }) => {
          return <p className="text-nowrap">{value}</p>;
        },
      },
      {
        Header: "Request Date",
        accessor: "requestDate",
        Cell: ({ row }) => {
          const { requestDate, requestTime } = row.original;
          return (
            <p className="text-nowrap">
              {requestDate || "-"}
              {requestTime ? `, ${requestTime}` : ""}
            </p>
          );
        },
      },
      {
        Header: "State",
        accessor: "state",
        Cell: ({ value }) => {
          return <p className="text-nowrap">{value}</p>;
        },
      },
      {
        Header: "City",
        accessor: "city",
        Cell: ({ value }) => {
          return <p className="text-nowrap">{value}</p>;
        },
      },
      {
        Header: "Area",
        accessor: "area",
        Cell: ({ value }) => {
          return <p className="text-nowrap">{value || "-"}</p>;
        },
      },
      {
        Header: "Room Required Date",
        accessor: "roomRequiredDate",
        Cell: ({ value }) => {
          // API already sends DD/MM/YYYY, but re-format defensively in case
          // it ever comes back as an ISO date.
          if (!value) return <p className="text-nowrap">-</p>;
          const parsed = moment(value, ["DD/MM/YYYY", moment.ISO_8601], true);
          return (
            <p className="text-nowrap">
              {parsed.isValid() ? parsed.format("DD/MM/YYYY") : value}
            </p>
          );
        },
      },
      {
        Header: "Booked",
        accessor: "bookingStatus",
        Cell: ({ value }) => {
          const isBooked = value === "booked";
          return (
            <p
              className="text-nowrap"
              style={{ color: isBooked ? "#22C55E" : "#EF4444" }}
            >
              {isBooked ? "YES" : "NO"}
            </p>
          );
        },
      },
      {
        Header: "Notification sent",
        accessor: "notificationSent",
        Cell: ({ value }) => {
          const isYes = value === "YES" || value === true;
          return (
            <p
              className="text-nowrap"
              style={{ color: isYes ? "#22C55E" : "#EF4444" }}
            >
              {isYes ? "YES" : "NO"}
            </p>
          );
        },
      },
      {
        Header: "WhatsApp sent",
        accessor: "whatsappSent",
        Cell: ({ value }) => {
          const isYes = value === "YES" || value === true;
          return (
            <p
              className="text-nowrap"
              style={{ color: isYes ? "#22C55E" : "#EF4444" }}
            >
              {isYes ? "YES" : "NO"}
            </p>
          );
        },
      },
      {
        Header: "Reminder Status",
        accessor: "reminderStatus",
        Cell: ({ value }) => {
          const isActiveReminder = value === "ACTIVE";
          return (
            <p
              className="text-nowrap"
              style={{ color: isActiveReminder ? "#22C55E" : "#9CA3AF" }}
            >
              {value || "-"}
            </p>
          );
        },
      },
    ];

    let action = {
      Header: "Action",
      accessor: "",
      Cell: ({ row }) => {
        return (
          <React.Fragment>
            <div className="d-flex ac-jc gap-3">
              <button
                onClick={() => {
                  navigate("/booking-request-form", {
                    state: { bookingData: row.original },
                  });
                }}
                className="cust-btn"
                style={{
                  backgroundColor: "#F3E8FD",
                  borderRadius: "50%",
                  width: 28,
                  height: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ color: "#AE55F5", fontWeight: "bold" }}>
                  &#8942;
                </span>
              </button>
            </div>
          </React.Fragment>
        );
      },
    };

    return [...d, action];
  }, [page, isActive, navigate]);

  return (
    <div>
      <Topbar header="AI-Booked History  " />
      <h2 className=" f3 fs-sm-16 fs-md-20 fs-lg-22 fs-xl-24 fs-xxl-26 ">
        All User
      </h2>
      <p style={{ color: "#6B7280" }}>
        Total Bookings: {count || propertiesList.length}
      </p>
      <div
        style={{
          marginTop: 10,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2,1fr)",
            gap: 2,
          }}
        >
          {["Not Booked", "Booked"].map((item, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #E5E7EB",
                backgroundColor: isActive === item ? "#AE55F5" : "white",
                cursor: "pointer",
                padding: "10px 10px",
                textAlign: "center",
                justifyContent: "center",
                borderRadius: 9,
              }}
              className="d-flex align-items-center"
              onClick={() => {
                setIsActive(item);
                setPage(1);
                setpaginationData((prev) => ({
                  ...prev,
                  [path]: {
                    ...prev?.[path],
                    active: item,
                    page: 1,
                  },
                }));
              }}
            >
              <p
                style={{
                  color: isActive === item ? "white" : "#898989",
                }}
              >
                {item}
              </p>
            </div>
          ))}
        </div>
        <div className="search-input">
          <input
            onChange={handleSearchChange}
            type="text"
            placeholder="Search"
            style={{ border: "none", width: "180px" }}
          />
          <img src={Search_icon} className="search here" alt="search" />
        </div>
        <div
          className=" mt-3 d-flex  ac-jc gap-4"
          style={{ flexDirection: "column" }}
        >
          <DateRangeMUI
            setFromDate={(date) => {
              setPage(1);
              const m = moment(date);
              setFromDate(m.isValid() ? m.format("YYYY-MM-DD") : null);
            }}
            setToDate={(date) => {
              setPage(1);
              const m = moment(date);
              setToDate(m.isValid() ? m.format("YYYY-MM-DD") : null);
            }}
          />
        </div>
      </div>
      <Table
        columns={columns}
        setPageno={(p) => {
          setPage(p);
          setpaginationData((prev) => ({
            ...prev,
            [path]: {
              ...prev?.[path],
              page: p,
            },
          }));
        }}
        pagecount={pagination}
        data={propertiesList}
        show={!loading}
      />
    </div>
  );
}

export default AiBookingHistory;
